import { Menu, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { t } from "@/utils/translations";
import MegaMenu from "./MegaMenu";
import FlipLogo from "./FlipLogo";
import { SIGNUP_URL } from "@/utils/constants";
import { trackSignupStarted, trackCtaClick, trackGenerateLead } from "@/utils/analytics";
import { useSignupModal } from "@/contexts/SignupModalContext";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { language, isRTL, getLocalizedPath } = useLanguage();
  const { openSignupModal } = useSignupModal();

  const handleSignup = () => {
    trackSignupStarted(t('headerSignup', language));
    trackCtaClick(t('headerSignup', language), 'header');
    trackGenerateLead('header');
    openSignupModal('header');
  };

  const handleFreeTrial = () => {
    trackSignupStarted('Start Free Trial');
    trackCtaClick('Start Free Trial', 'header');
    trackGenerateLead('header_free_trial');
    openSignupModal('header_free_trial');
  };

  // Helper to get the correct link path based on current language
  const getLink = (path: string) => getLocalizedPath(path);

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 w-full shadow-sm">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 md:h-20 items-center justify-between">
            <Link to={getLink("/")} className="flex items-center space-x-3 rtl:space-x-reverse">
              <FlipLogo size="md" />
              <span className="text-2xl font-bold text-black hidden sm:inline">Flip For Business</span>
            </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8 rtl:space-x-reverse">
            <MegaMenu title={t('solutions', language)} isRTL={isRTL}>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">{t('paymentSolutions', language)}</h3>
                  <div className="space-y-3">
                    <Link to={getLink("/solutions/cross-border-payments")} className="block text-gray-600 hover:text-black transition-colors">
                      {t('crossBorderPayments', language)}
                    </Link>
                    <Link to={getLink("/solutions/freelancer-payments")} className="block text-gray-600 hover:text-black transition-colors">
                      {t('freelancerPayments', language)}
                    </Link>
                    <Link to={getLink("/solutions/bulk-payments")} className="block text-gray-600 hover:text-black transition-colors">
                      {t('bulkPayments', language)}
                    </Link>
                    <Link to={getLink("/eor-payroll-platforms")} className="block text-gray-600 hover:text-black transition-colors">
                      EOR & Payment Platforms
                    </Link>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">{t('about', language)}</h3>
                  <div className="space-y-3">
                    <Link to={getLink("/features")} className="block text-gray-600 hover:text-black transition-colors">
                      {t('features', language)}
                    </Link>
                    <Link to={getLink("/pricing")} className="block text-gray-600 hover:text-black transition-colors">
                      {t('pricing', language)}
                    </Link>
                    <Link to={getLink("/europe")} className="block text-gray-600 hover:text-black transition-colors">
                      Europe payouts
                    </Link>
                  </div>
                </div>
              </div>
            </MegaMenu>

            <MegaMenu title={t('industries', language)} isRTL={isRTL}>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">{t('serviceIndustries', language)}</h3>
                  <div className="space-y-3">
                    <Link to={getLink("/industries/freelancer-platforms")} className="block text-gray-600 hover:text-black transition-colors">
                      {t('freelancerPlatforms', language)}
                    </Link>
                    <Link to={getLink("/industries/recruitment-agencies")} className="block text-gray-600 hover:text-black transition-colors">
                      {t('recruitmentAgencies', language)}
                    </Link>
                    <Link to={getLink("/industries/consulting")} className="block text-gray-600 hover:text-black transition-colors">
                      {t('consultingFirms', language)}
                    </Link>
                  </div>
                </div>
                <div>
                  <div className="space-y-3">
                    <Link to={getLink("/industries/tech-startups")} className="block text-gray-600 hover:text-black transition-colors">
                      {t('techStartups', language)}
                    </Link>
                    <Link to={getLink("/industries/creative-studios")} className="block text-gray-600 hover:text-black transition-colors">
                      {t('creativeStudios', language)}
                    </Link>
                    <Link to={getLink("/industries/e-commerce")} className="block text-gray-600 hover:text-black transition-colors">
                      {t('ecommerce', language)}
                    </Link>
                  </div>
                </div>
              </div>
            </MegaMenu>

            <MegaMenu title={t('company', language)} isRTL={isRTL}>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">{t('about', language)}</h3>
                  <div className="space-y-3">
                    <Link to={getLink("/about")} className="block text-gray-600 hover:text-black transition-colors">
                      {t('aboutUs', language)}
                    </Link>
                    <Link to={getLink("/why-flip")} className="block text-gray-600 hover:text-black transition-colors">
                      {t('whyChooseFlip', language)}
                    </Link>
                    <Link to={getLink("/contact")} className="block text-gray-600 hover:text-black transition-colors">
                      {t('contactUs', language)}
                    </Link>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">{t('resources', language)}</h3>
                  <div className="space-y-3">
                    <Link to={getLink("/blog")} className="block text-gray-600 hover:text-black transition-colors">
                      {t('blog', language)}
                    </Link>
                    <Link to={getLink("/resources")} className="block text-gray-600 hover:text-black transition-colors">
                      {t('resourceHub', language)}
                    </Link>
                  </div>
                </div>
              </div>
            </MegaMenu>

            <Link
              to={getLink("/contact")}
              className="text-gray-700 hover:text-black transition-colors font-medium"
            >
              {t('contactUs', language)}
            </Link>
          </nav>

          <div className="hidden md:flex items-center space-x-3 rtl:space-x-reverse">
            <a
              href={SIGNUP_URL}
              onClick={() => {
                trackSignupStarted(t('headerSignup', language));
                trackCtaClick(t('headerSignup', language), 'header');
                trackGenerateLead('header');
              }}
              className="text-foreground hover:text-primary transition-colors font-medium px-3 py-2"
            >
              Login
            </a>
            <button
              onClick={handleFreeTrial}
              className="btn-flip-primary flex items-center gap-2"
            >
              Start Free Trial
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-black"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-6 border-t border-gray-200">
            <nav className="flex flex-col space-y-6">
              <Link to={getLink("/solutions")} onClick={() => setIsMenuOpen(false)} className="text-gray-600 hover:text-gray-900 transition-colors font-medium px-2">
                {t('solutions', language)}
              </Link>
              <Link to={getLink("/industries")} onClick={() => setIsMenuOpen(false)} className="text-gray-600 hover:text-gray-900 transition-colors font-medium px-2">
                {t('industries', language)}
              </Link>
              <Link to={getLink("/europe")} onClick={() => setIsMenuOpen(false)} className="text-gray-600 hover:text-gray-900 transition-colors font-medium px-2">
                Europe payouts
              </Link>
              <Link to={getLink("/pricing")} onClick={() => setIsMenuOpen(false)} className="text-gray-600 hover:text-gray-900 transition-colors font-medium px-2">
                {t('pricing', language)}
              </Link>
              <Link to={getLink("/about")} onClick={() => setIsMenuOpen(false)} className="text-gray-600 hover:text-gray-900 transition-colors font-medium px-2">
                {t('about', language)}
              </Link>
              <Link to={getLink("/why-flip")} onClick={() => setIsMenuOpen(false)} className="text-gray-600 hover:text-gray-900 transition-colors font-medium px-2">
                {t('whyChooseFlip', language)}
              </Link>
              <Link to={getLink("/blog")} onClick={() => setIsMenuOpen(false)} className="text-gray-600 hover:text-gray-900 transition-colors font-medium px-2">
                {t('blog', language)}
              </Link>
              <Link to={getLink("/faq")} onClick={() => setIsMenuOpen(false)} className="text-gray-600 hover:text-gray-900 transition-colors font-medium px-2">
                {t('faqSupport', language)}
              </Link>
              <Link to={getLink("/contact")} onClick={() => setIsMenuOpen(false)} className="text-gray-600 hover:text-gray-900 transition-colors font-medium px-2">
                {t('contactUs', language)}
              </Link>
              <div className="pt-4 space-y-3">
                <a
                  href={SIGNUP_URL}
                  onClick={() => {
                    setIsMenuOpen(false);
                    trackSignupStarted(t('headerSignup', language));
                    trackCtaClick(t('headerSignup', language), 'header');
                    trackGenerateLead('header');
                  }}
                  className="block w-full text-center text-foreground hover:text-primary transition-colors font-medium py-2"
                >
                  Login
                </a>
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    handleFreeTrial();
                  }}
                  className="btn-flip-primary w-full flex items-center justify-center gap-2"
                >
                  Start Free Trial
                </button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
